import { useCallback, useEffect, useRef, useState } from 'react';

export interface DraftSelectionApi<T> {
  /** Current draft selection (uncommitted). */
  readonly draft: readonly T[];
  /** True if `value` is in the current draft. */
  readonly isSelected: (value: T) => boolean;
  /** Toggle a single value in the draft. */
  readonly toggle: (value: T) => void;
  /** Replace the entire draft. */
  readonly setDraft: (next: readonly T[]) => void;
  /** Open the editor: re-seed the draft from the committed selection. */
  readonly open: () => void;
  /** Commit the draft to the parent via `onCommit` and close. */
  readonly commit: () => void;
  /** Discard the draft and re-seed from the committed selection. */
  readonly cancel: () => void;
}

export interface UseDraftSelectionOptions<T> {
  /** The currently committed selection that the draft is based on. */
  readonly committed: readonly T[];
  /** Called when the user explicitly commits the draft (e.g. taps Done). */
  readonly onCommit: (next: readonly T[]) => void;
}

/**
 * Transactional multi-select state for editors that should "stage" changes
 * (typical pattern for a multi-select inside a modal or flyout).
 *
 * Selections are held locally and only propagated to the parent when
 * `commit()` is called. Calling `cancel()` (or letting the editor close
 * without committing) re-seeds the draft from the committed value.
 *
 * The hook also keeps the draft in sync if the committed value changes
 * externally while the editor is closed (e.g. another control on the page
 * clears a selection). It does not stomp on an open editor.
 *
 * @example
 *   const draft = useDraftSelection({
 *     committed: selectedCodes,
 *     onCommit: onChange,
 *   });
 *
 *   <Trigger onClick={draft.open}>...</Trigger>
 *   <Modal
 *     onClose={draft.cancel}
 *     buttons={[<Button onClick={draft.commit}>Done</Button>]}
 *   >
 *     {items.map((item) => (
 *       <Checkbox
 *         checked={draft.isSelected(item.id)}
 *         onChange={() => draft.toggle(item.id)}
 *       />
 *     ))}
 *   </Modal>
 */
export function useDraftSelection<T>({
  committed,
  onCommit,
}: UseDraftSelectionOptions<T>): DraftSelectionApi<T> {
  const [draft, setDraftState] = useState<readonly T[]>(committed);
  const isEditingRef = useRef(false);

  // Re-seed from committed whenever it changes from outside while the
  // editor is closed. While editing, leave the draft alone.
  useEffect(() => {
    if (!isEditingRef.current) {
      setDraftState(committed);
    }
  }, [committed]);

  const setDraft = useCallback((next: readonly T[]) => setDraftState(next), []);

  const toggle = useCallback((value: T) => {
    setDraftState((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  }, []);

  const isSelected = useCallback((value: T) => draft.includes(value), [draft]);

  const open = useCallback(() => {
    isEditingRef.current = true;
    setDraftState(committed);
  }, [committed]);

  const commit = useCallback(() => {
    isEditingRef.current = false;
    onCommit(draft);
  }, [draft, onCommit]);

  const cancel = useCallback(() => {
    isEditingRef.current = false;
    setDraftState(committed);
  }, [committed]);

  return { draft, isSelected, toggle, setDraft, open, commit, cancel };
}
